import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { detectTypedFrontmatter } from '../services/frontmatterText'

// One definition of a word for everything that measures writing: the same
// rule TipTap's CharacterCount uses for the word readout, so counts from an
// open editor and from a page read off disk always agree.

function wordsIn(node: ProseMirrorNode): number {
  return node.textBetween(0, node.content.size, ' ', ' ').split(' ').filter((word) => word !== '').length
}

/** Leading nodes that hold front matter typed at the top of the page. */
function frontmatterNodeCount(doc: ProseMirrorNode): number {
  const first = doc.childCount ? doc.firstChild : null
  const mayHaveFrontmatter = first && (first.type.name === 'horizontalRule' || /^\s*(---|[—–]-|\+\+\+)/.test(first.textContent))
  if (!mayHaveFrontmatter) return 0
  // Only the leading nodes are serialized, so this stays cheap on long documents.
  const leading: any[] = []
  for (let index = 0; index < Math.min(doc.childCount, 80); index++) leading.push(doc.child(index).toJSON())
  return detectTypedFrontmatter(leading)?.nodeCount ?? 0
}

/** Words in a document, leaving out front matter typed at the top. */
export function countDocWords(doc: ProseMirrorNode): number {
  let words = wordsIn(doc)
  const skip = frontmatterNodeCount(doc)
  for (let index = 0; index < skip; index++) words -= wordsIn(doc.child(index))
  return Math.max(0, words)
}

/** Words and characters in the editor's document, as the word-count readout shows them. */
export function countEditorWords(editor: any): { words: number; characters: number } {
  const doc = editor?.state?.doc as ProseMirrorNode | undefined
  const counter = editor?.storage?.characterCount
  if (!doc || !counter) return { words: 0, characters: 0 }

  let characters = counter.characters()
  const skip = frontmatterNodeCount(doc)
  for (let index = 0; index < skip; index++) characters -= counter.characters({ node: doc.child(index) })
  return { words: countDocWords(doc), characters: Math.max(0, characters) }
}
